import React from "react";
import { Animated, Easing, Dimensions, Image, Platform } from "react-native";
import { defineMessages } from "react-intl";
import { graphql, Mutation, Query, withApollo } from "react-apollo";
import styled, { css, withTheme } from "styled-components/native";
import { Button, Toast, StickyDrawer, VerticalMenu } from "cci-component";
import { getSessionInformation } from "cci-data";
import { hasPermissions, logoutMutation } from "cci-logic";
import { withRouter } from "cci-utility";
import GetMainMenuInfoQuery from "../query/GetMainMenuInfoQuery";
import Header from "./Header";
import UserInfo from "./UserInfo";
import BusinessSelectionDialog from "../view/business/SelectionDialog";
import OfficeSelectionDialog from "../view/office/SelectionDialog";

/**
 * 768 is the lg breakpoint in react-native-responsive-layout.
 *
 * It is used to show the user info in the drawer whene mobile.
 */
const MENU_BREAKPOINT = 768;

const messages = defineMessages({
  logout: {
    id: "Logout",
    defaultMessage: "Logout"
  }
});

const StyledStickyDrawer = styled(StickyDrawer)`
  background-color: ${props => props.theme.mainMenuColor};

  /* Sadly React Native displays the shadow because our background is semi-transparent, this is noticable if you set the shadow to red (for testing) */
  box-shadow: ${props => props.theme.mainMenuShadow};
`;

/* General layout for the main view that contains the page contents */
const wrapperCss = css`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  ${props => Platform.OS === "web" && `background-repeat: no-repeat`};
  ${props =>
    props.theme.pageBackgroundColor &&
    `background-color: ${props.theme.pageBackgroundColor}`};
  ${props =>
    props.theme.pageBackgroundImage &&
    `background-image: ${props.theme.pageBackgroundImage}`};
  ${props =>
    props.theme.pageBackgroundPosition &&
    `background-position: ${props.theme.pageBackgroundPosition}`};
  ${props =>
    props.theme.pageBackgroundSize &&
    `background-size: ${props.theme.pageBackgroundSize}`};
`;

const Wrapper = styled.View`
  ${wrapperCss};
`;

/* This animated copy allows us to animate the page contents when the menu is open */
const AnimatedWrapper = styled(Animated.View)`
  ${wrapperCss};
`;

/**
 * Base for all pages where the user has been authenticated (a valid user id and password has been specified).
 */
class PrimaryPage extends React.Component {
  constructor(props) {
    super(props);
    this.initialScale = 0.9;

    this.state = {
      // Determines initially if the user info should be shown in the drawer, for mobile devices.
      showNorth: Dimensions.get("window").width < MENU_BREAKPOINT,

      showBusinessSelection: false,
      showOfficeSelection: false
    };

    this.scaleFactor = new Animated.Value(1);

    this.showSubMenu = this.showSubMenu.bind(this);
    this.onDrawerStateChanged = this.onDrawerStateChanged.bind(this);
    this.scale = this.scale.bind(this);
    this.hideDrawer = this.hideDrawer.bind(this);
    this.showBusinessSelection = this.showBusinessSelection.bind(this);
    this.showOfficeSelection = this.showOfficeSelection.bind(this);
    this.handleBusinessSelected = this.handleBusinessSelected.bind(this);
    this.hideSelectionDialogsAndRedirect = this.hideSelectionDialogsAndRedirect.bind(
      this
    );
    this.onLayout = this.onLayout.bind(this);
  }

  /**
   * Pushes a submenu to the drawer so that it displays to the right of the current menu.
   */
  showSubMenu(parentMenu, subMenu, columnWidth) {
    const key = parentMenu + 1;

    this.refs.drawer.push(
      parentMenu,

      <VerticalMenu
        key={key}
        menu={subMenu}
        onShowSubMenu={subMenu => this.showSubMenu(key, subMenu, columnWidth)}
        onNavigate={() => this.hideDrawer()}
        style={{ width: columnWidth }}
      />
    );

    //For every column added to the drawer, we scale the children (page content) by .1
    this.scale(this.initialScale - key * 0.1);
  }

  /**
   * Fired when the drawer is open or closed.
   */
  onDrawerStateChanged(isOpen) {
    //Scale the children either when the drawer is initially open or when the drawer is closed
    this.scale(isOpen ? this.initialScale : 1);
  }

  /**
   * Animates the scaling of the children (page content) by the factor specified.
   */
  scale(factor) {
    Animated.timing(this.scaleFactor, {
      toValue: factor,
      duration: 500,
      easing: Easing.out(Easing.quad)
    }).start();
  }

  /**
   * Shows the business selection dialog.
   */
  showBusinessSelection() {
    this.setState({
      // Show the business dialog
      showBusinessSelection: true
    });
  }

  /**
   * Shows the office selection dialog.
   */
  showOfficeSelection() {
    this.setState({
      // Show the office dialog
      showOfficeSelection: true
    });
  }

  /**
   * Hides the business selection dialog and shows the office selection dialog with a timeout to handle an issue on mobile.
   */
  handleBusinessSelected() {
    const { data, history } = this.props,
      officePermitted =
        data.sessionInformation &&
        hasPermissions(data.sessionInformation.user.permissions, {
          objects: "Office",
          actions: ["selectOffice"]
        });

    this.setState({
      // Hide the business dialog
      showBusinessSelection: false
    });

    if (officePermitted) {
      setTimeout(this.showOfficeSelection, 450);
    } else {
      setTimeout(history.replace("/"), 500);
    }
  }

  /**
   * Hides both the business and office selection dialogs.
   */
  hideSelectionDialogs() {
    this.setState({
      // Hide the business dialog
      showBusinessSelection: false,

      // Hide the office dialog
      showOfficeSelection: false
    });
  }

  /**
   * Hides both the business and office selection dialogs.
   */
  hideSelectionDialogsAndRedirect() {
    this.setState({
      // Hide the business dialog
      showBusinessSelection: false,

      // Hide the office dialog
      showOfficeSelection: false
    });

    this.props.history.replace("/");
  }

  /**
   * Hides the main menu with an animation.
   */
  hideDrawer() {
    this.props.client.writeData({
      data: {
        MainMenu: {
          __typename: "MainMenu",
          visible: false
        }
      }
    });
  }

  /**
   * Fired when the screen layout changes, we use it to show/hide the user info in the drawer when in mobile.
   */
  onLayout(e) {
    const width = e.nativeEvent.layout.width,
      showNorth = width < MENU_BREAKPOINT;

    if (this.state.showNorth !== showNorth) {
      this.setState({ showNorth });
    }
  }

  render() {
    const { props, showSubMenu, onDrawerStateChanged } = this,
      officePermitted =
        props.data.sessionInformation &&
        hasPermissions(props.data.sessionInformation.user.permissions, {
          objects: "Office",
          actions: ["selectOffice"]
        });

    return (
      <Toast>
        {/* Outer wrapper keeps the background color in place for mobile devices even when scaled */}
        <Wrapper onLayout={this.onLayout}>
          <AnimatedWrapper
            style={{
              transform: [{ scale: this.scaleFactor }]
            }}
          >
            {props.children}
          </AnimatedWrapper>
        </Wrapper>

        {/* Main navigation drawer that expands for every submenu */}
        {/* TODO FIX THIS: https://fb.me/react-strict-mode-string-ref*/}
        <Query query={GetMainMenuInfoQuery} errorPolicy="ignore">
          {({ loading, error, data }) => (
            <StyledStickyDrawer
              ref="drawer"
              initialWidth={props.theme.sidebarWidth}
              isOpen={data.MainMenu.visible}
              onStateChanged={onDrawerStateChanged}
            >
              {({ columnWidth }) => (
                <VerticalMenu
                  margins="100px 0 0 0"
                  flex={1}
                  width={`${columnWidth}px`}
                  northStyle={{ marginBottom: 20 }}
                  north={
                    this.state.showNorth && (
                      <UserInfo
                        avatarSize={55}
                        userFontSize="20px"
                        businessFontSize="13px"
                        infoAlign="center"
                        infoMargins="8px 0 0 0"
                        onBusinessTap={() => this.showBusinessSelection()}
                        onOfficeTap={() => this.showOfficeSelection()}
                      />
                    )
                  }
                  items={data.MainMenu.items}
                  southStyle={{
                    padding: 20
                  }}
                  south={
                    <React.Fragment>
                      {
                        <Image
                          source={
                            props.theme.hideCciLogo
                              ? null
                              : require("../assets/images/cciLogo.png")
                          }
                          style={{ width: 60, height: 20 }}
                        />
                      }

                      <Mutation mutation={logoutMutation}>
                        {logout => (
                          <Button
                            ui="link"
                            leftIcon="sign-out"
                            fontColor={
                              props.theme.mainMenuFontColor ||
                              props.theme.fontColor
                            }
                            title={messages.logout}
                            onPress={() => {
                              logout({
                                errorPolicy: "ignore",
                                context: { client: props.client }
                              });
                              props.history.push("/");
                            }}
                          />
                        )}
                      </Mutation>
                    </React.Fragment>
                  }
                  onShowSubMenu={subMenu =>
                    showSubMenu(0, subMenu, columnWidth)
                  }
                  onNavigate={() => this.hideDrawer()}
                />
              )}
            </StyledStickyDrawer>
          )}
        </Query>

        {/* Hamburger logo, menu button and session information */}
        <Header
          onBusinessTap={() => this.showBusinessSelection()}
          onOfficeTap={() => this.showOfficeSelection()}
          hideHamburguer={props.hideHamburguer}
          hideUserInfo={props.hideUserInfo}
        />

        {/* Modal for business selection */}
        <BusinessSelectionDialog
          isVisible={this.state.showBusinessSelection}
          onBackdropPress={() => this.hideSelectionDialogs()}
          onSelected={() => this.handleBusinessSelected()}
        />

        {/* Modal for office selection */}
        <OfficeSelectionDialog
          isVisible={this.state.showOfficeSelection && officePermitted}
          onBackdropPress={() => this.hideSelectionDialogs()}
          onSelected={() => this.hideSelectionDialogsAndRedirect()}
        />
      </Toast>
    );
  }
}

export default withRouter(
  withTheme(
    withApollo(
      graphql(getSessionInformation, { fetchPolicy: "cache-only" })(PrimaryPage)
    )
  )
);
